/**
 * A show style, read structurally (D32): its abstract layers (the drivers
 * each allows, the piece types using it), source and output layers, piece
 * types with their params and the layers their templates target, macro
 * actions with their steps, and adlib panels. The JSON editor beside it is
 * where it changes.
 */
import type { ReactNode } from 'react';
import type { ShowStyleSummaryView } from '../../lib/showStyle/config.js';
import { Badge } from '../ui/badge.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ShowStyleSummaryProps = { readonly summary: ShowStyleSummaryView };

function Section({ title, count, children }: { readonly title: string; readonly count: number; readonly children: ReactNode }) {
  return (
    <section className="grid gap-2" aria-label={title}>
      <h3 className="text-sm font-semibold">
        {title} <span className="font-normal text-muted-foreground">({count})</span>
      </h3>
      {count === 0 ? <p className="text-sm text-muted-foreground">None.</p> : children}
    </section>
  );
}

export function ShowStyleSummary({ summary }: ShowStyleSummaryProps) {
  return (
    <div className="grid gap-6" data-testid="show-style-summary">
      <Section title="Layers" count={summary.layers.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Layer</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Needs</TableHead>
              <TableHead>Used by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.layers.map((l) => (
              <TableRow key={l.id} data-testid="show-style-layer-row">
                <TableCell className="font-mono">{l.id}</TableCell>
                <TableCell>{l.label}</TableCell>
                <TableCell className="text-xs">{l.deviceTypes.length === 0 ? 'any driver' : l.deviceTypes.join(', ')}</TableCell>
                <TableCell className="text-xs">{l.usedBy.length === 0 ? '–' : l.usedBy.join(', ')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Source layers" count={summary.sourceLayers.length}>
          <ul className="grid gap-1 text-sm">
            {summary.sourceLayers.map((l) => (
              <li key={l.id}>
                <span className="font-mono">{l.id}</span> {l.label === '' ? null : <span className="text-muted-foreground">— {l.label}</span>}
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Output layers" count={summary.outputLayers.length}>
          <ul className="grid gap-1 text-sm">
            {summary.outputLayers.map((l) => (
              <li key={l.id} className="flex items-center gap-2">
                <span className="font-mono">{l.id}</span>
                {l.label === '' ? null : <span className="text-muted-foreground">— {l.label}</span>}
                {l.isPgm ? <Badge variant="onair">PGM</Badge> : null}
              </li>
            ))}
          </ul>
        </Section>
      </div>
      <Section title="Piece types" count={summary.pieceTypes.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Id</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Source layer</TableHead>
              <TableHead>Lifespan</TableHead>
              <TableHead>Params</TableHead>
              <TableHead>Timeline layers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.pieceTypes.map((t) => (
              <TableRow key={t.id} data-testid="piece-type-row">
                <TableCell className="font-mono">{t.id}</TableCell>
                <TableCell>{t.label}</TableCell>
                <TableCell className="font-mono">{t.sourceLayer}</TableCell>
                <TableCell>{t.lifespan}</TableCell>
                <TableCell className="text-xs">{t.params.length === 0 ? '–' : t.params.map((p) => `${p.name}: ${p.type}${p.required ? '*' : ''}`).join(', ')}</TableCell>
                <TableCell className="font-mono text-xs">{t.layers.length === 0 ? '(virtual)' : t.layers.join(', ')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Actions" count={summary.actions.length}>
          <ul className="grid gap-1 text-sm">
            {summary.actions.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.label === '' ? a.id : a.label}</span> <span className="text-muted-foreground">{a.steps.join(' → ')}</span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Adlib panels" count={summary.adlibPanels.length}>
          <ul className="grid gap-1 text-sm">
            {summary.adlibPanels.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.label === '' ? p.id : p.label}</span> <span className="text-muted-foreground">{p.items.join(' · ')}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
}
